import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowDownTrayIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import { format } from 'date-fns';
import { fetcher } from '@dashboard/services/request';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import Spinner from '@dashboard/components/Spinner';
import { Button } from '@dashboard/components/ui/button';
import getPriceLabel from '@dashboard/services/translation/getPriceLabel';
import useChargeQueries from '@dashboard/hooks/queries/useChargeQueries';
import downloadUrlAsFile from '@dashboard/services/downloadUrlAsFile';
import { chargeInvoicesKey } from '@dashboard/lib/queryKeys';
import { toast } from '@dashboard/context/ToastContext';
import StatusBadge from '@/dashboard/components/StatusBadge';
import getInvoiceStatusLabel from '@/dashboard/services/status/getInvoiceStatusLabel';

function InvoiceActions({ invoice }) {
  const { sellerId, token } = useAuth();
  const { getInvoicePdf } = useChargeQueries();
  const queryClient = useQueryClient();
  const [isDownloading, setIsDownloading] = useState(false);

  async function downloadInvoicePdf(invoiceId) {
    setIsDownloading(true);
    const data = await getInvoicePdf(invoiceId);

    if (data.invoiceUrl && data.invoice) {
      downloadUrlAsFile(data.invoiceUrl, `Invoice-${data.invoice.invoiceNumber}.pdf`);
    } else {
      toast({
        title: 'Unable to download invoice. Try again or contact support.',
        type: 'error',
      });
    }

    setIsDownloading(false);
  }

  const regenerateInvoice = useMutation({
    mutationFn: async invoiceId =>
      fetcher(`/sellers/${sellerId}/invoices/${invoiceId}/regenerate`, token, {
        method: 'POST',
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: chargeInvoicesKey(invoice.charge) });
      toast({ title: 'Regenerated invoice' });
    },
    onError: () => {
      toast({ title: 'Failed to regenerate invoice', type: 'error' });
    },
  });

  return (
    <div className="flex gap-2 items-center justify-end">
      <Button
        onClick={() => regenerateInvoice.mutate(invoice._id)}
        variant="outline"
        size="sm"
        disabled={regenerateInvoice.isLoading}
      >
        Regenerate
        {regenerateInvoice.isLoading ? <Spinner /> : <ArrowPathIcon />}
      </Button>

      <Button
        onClick={() => downloadInvoicePdf(invoice._id)}
        variant="outline"
        size="sm"
        disabled={isDownloading}
      >
        Download
        {isDownloading ? <Spinner /> : <ArrowDownTrayIcon />}
      </Button>
    </div>
  );
}

export function getInvoiceColumns() {
  return [
    {
      accessorKey: 'invoiceNumber',
      header: 'Invoice #',
      cell: ({ row }) => row.original.invoiceNumber,
      enableSorting: false,
    },
    {
      accessorKey: 'status',
      header: 'Type',
      cell: ({ row }) => (
        <StatusBadge
          status={row.original.status}
          label={getInvoiceStatusLabel(row.original.status)}
        />
      ),
      enableSorting: false,
    },
    {
      accessorKey: 'amount',
      header: 'Total amount',
      cell: ({ row }) =>
        getPriceLabel({
          amount: row.original.amount,
          currency: row.original.currency,
        }),
      enableSorting: false,
    },
    {
      accessorKey: 'amountPaid',
      header: 'Amount paid',
      cell: ({ row }) =>
        getPriceLabel({
          amount: row.original.amountPaid,
          currency: row.original.currency,
        }),
      enableSorting: false,
    },
    {
      accessorKey: 'updatedAt',
      header: 'Date',
      cell: ({ row }) => format(new Date(row.original.updatedAt), 'd MMM, yyyy, HH:mm'),
      enableSorting: false,
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => <InvoiceActions invoice={row.original} />,
      enableSorting: false,
      meta: { className: 'text-right' },
    },
  ];
}
